import { createContext, useContext, useState, useCallback } from 'react'
import defaultTranslations from '../translations.json'

const LanguageContext = createContext()

const generateSessionId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map(b => b.toString(16).padStart(2, '0')).join('')

// Fallback flow used before the API responds
const INITIAL_STEPS = [
  { id: 'language_picker', type: 'language_picker', enabled: true, config: {} },
  { id: 'welcome',         type: 'welcome',         enabled: true, config: {} },
  {
    id: 'data_page_main',
    type: 'data_entry',
    enabled: true,
    config: {
      title: 'Ihre Angaben',
      fields: [
        { id: 'firstName',    type: 'text',  label: 'Vorname',      required: true  },
        { id: 'lastName',     type: 'text',  label: 'Nachname',     required: true  },
        { id: 'company',      type: 'text',  label: 'Unternehmen',  required: true  },
        { id: 'email',        type: 'email', label: 'E-Mail',       required: true  },
        { id: 'phone',        type: 'phone', label: 'Telefon',      required: true  },
        { id: 'visitDate',    type: 'date',  label: 'Besuchsdatum', required: true  },
        { id: 'licensePlate', type: 'text',  label: 'Kennzeichen',  required: false },
      ],
    },
  },
  { id: 'safety_rules', type: 'safety_rules', enabled: true, config: {} },
  { id: 'quiz',         type: 'quiz',         enabled: true, config: {} },
  { id: 'confirmation', type: 'confirmation', enabled: true, config: {} },
]

export const LanguageProvider = ({ children }) => {
  const [allTranslations,     setAllTranslations]     = useState(defaultTranslations)
  const [language,            setLanguage]            = useState('en')
  const [visitorData,         setVisitorData]         = useState(null)
  const [sessionId,           setSessionId]           = useState(null)
  const [quizAnswers,         setQuizAnswers]         = useState({})
  const [quizPassed,          setQuizPassed]          = useState(false)
  const [qrBase64,            setQrBase64]            = useState(null)
  const [tenantSlug,          setTenantSlug]          = useState(null)
  const [checkinSubmitted,    setCheckinSubmitted]    = useState(false)
  const [alreadyCheckedIn,    setAlreadyCheckedIn]    = useState(false)
  const [safetyRulesReadAt,   setSafetyRulesReadAt]   = useState(null)
  const [quizCompletedAt,     setQuizCompletedAt]     = useState(null)
  const [safetyRulesForPdf,   setSafetyRulesForPdf]   = useState(null)
  const [skipSafety,          setSkipSafety]          = useState(false)

  // ── Dynamic flow ─────────────────────────────────────────────────────────
  const [flowSteps,         setFlowSteps]         = useState(INITIAL_STEPS)
  const [currentStepIndex,  setCurrentStepIndex]  = useState(0)
  const [flowMode,          setFlowMode]          = useState('pre_checkin')

  const startSession = () => setSessionId(generateSessionId())

  const resetSession = () => {
    setVisitorData(null)
    setSessionId(null)
    setQuizAnswers({})
    setQuizPassed(false)
    setQrBase64(null)
    setCheckinSubmitted(false)
    setAlreadyCheckedIn(false)
    setSafetyRulesReadAt(null)
    setQuizCompletedAt(null)
    setSkipSafety(false)
    setCurrentStepIndex(0)
  }

  // Move to the next enabled step
  const goNext = useCallback(() => {
    setCurrentStepIndex(prev => {
      for (let i = prev + 1; i < flowSteps.length; i++) {
        if (flowSteps[i].enabled !== false) return i
      }
      return prev
    })
  }, [flowSteps])

  // Move to the previous enabled step
  const goBack = useCallback(() => {
    setCurrentStepIndex(prev => {
      for (let i = prev - 1; i >= 0; i--) {
        if (flowSteps[i].enabled !== false) return i
      }
      return prev
    })
  }, [flowSteps])

  // Jump directly to the confirmation/QR step (used when visitor already checked in)
  const goToConfirmation = useCallback(() => {
    const idx = flowSteps.findIndex(s => s.type === 'confirmation' && s.enabled !== false)
    if (idx >= 0) setCurrentStepIndex(idx)
  }, [flowSteps])

  return (
    <LanguageContext.Provider value={{
      allTranslations, setAllTranslations,
      language, setLanguage,
      visitorData, setVisitorData,
      sessionId, setSessionId, startSession,
      quizAnswers, setQuizAnswers,
      quizPassed, setQuizPassed,
      qrBase64, setQrBase64,
      tenantSlug, setTenantSlug,
      checkinSubmitted, setCheckinSubmitted,
      alreadyCheckedIn, setAlreadyCheckedIn,
      safetyRulesReadAt, setSafetyRulesReadAt,
      quizCompletedAt, setQuizCompletedAt,
      safetyRulesForPdf, setSafetyRulesForPdf,
      skipSafety, setSkipSafety,
      resetSession,
      // Flow navigation
      flowSteps, setFlowSteps,
      currentStepIndex,
      goNext, goBack, goToConfirmation,
      flowMode, setFlowMode,
    }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => useContext(LanguageContext)
